import 'react-native-reanimated';
import '../globals.css';

import messaging, {
  FirebaseMessagingTypes,
  onMessage,
  onNotificationOpenedApp,
  setBackgroundMessageHandler
} from '@react-native-firebase/messaging';
import { DefaultTheme, ThemeProvider } from "expo-router/react-navigation";
import * as Sentry from '@sentry/react-native';
import { reactNativeTracingIntegration, reactNavigationIntegration } from '@sentry/react-native';
import { focusManager, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as Linking from 'expo-linking';
import * as Notifications from 'expo-notifications';
import { Href, router, Slot, usePathname } from 'expo-router';
import * as SplashScreen from 'expo-splash-screen';
import * as WebBrowser from 'expo-web-browser';
import { useEffect } from 'react';
import { AppState, AppStateStatus, Platform, StyleSheet } from 'react-native';
import appsFlyer from 'react-native-appsflyer';
import { SystemBars } from 'react-native-edge-to-edge';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { PaperProvider } from 'react-native-paper';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import Toast from 'react-native-toast-message';

import SharedBottomSheet from '@/components/bottomSheets/SharedBottomSheet';
import EndFlowModal from '@/components/modals/EndFlowModal';
import InfoModal from '@/components/modals/InfoModal';
import { APPSFLYER_CONFIG, ENV, SENTRY_DSN } from '@/constants/config';
import { AuthProvider } from '@/contexts/authContext';
import { BottomSheetProvider } from '@/contexts/bottomSheetContext';
import { EndFlowModalProvider } from '@/contexts/endFlowModalContext';
import { FavoritesProvider } from '@/contexts/favoritesContext';
import { FiltersProvider } from '@/contexts/filtersContext';
import { InfoModalProvider } from '@/contexts/infoModalContext';
import { NpoLoginScreenStateProvider } from '@/contexts/npoLoginScreenStateContext';
import { initWebAnalytics } from '@/firebaseConfig';
import { AppStorage } from '@/utils/AppStorage';
import { parseQueryParams } from '@/utils/parseQueryParams';
import { safeJsonParse } from '@/utils/jsonParse';
import { RoomData } from '@/api/models/chatRoomModels';
import { setPendingLink } from '@/deepLinks/deepLinkStore';
import { parseNativeDeepLink, parseNativeDeepLinkWithParams } from '@/deepLinks/parseDeepLink';

// Prevent the splash screen from auto-hiding before asset loading is complete.
SplashScreen.preventAutoHideAsync();

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 2 } }
});

// Initialize Sentry
if (!__DEV__) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: ENV,
    debug: ENV !== 'production', // If `true`, Sentry will try to print out useful debugging information if something goes wrong with sending the event. Set it to `false` in production
    integrations: [reactNavigationIntegration(), reactNativeTracingIntegration()]
  });
}

if (Platform.OS !== 'web') {
  // Initialize appsFlyer
  appsFlyer.initSdk(
    {
      devKey: process.env.EXPO_PUBLIC_APPFLYER_DEVELOPER_KEY ?? '',
      isDebug: __DEV__,
      appId:
        Platform.OS === 'ios'
          ? process.env.EXPO_PUBLIC_APPFLYER_IOS_APP_ID
          : process.env.EXPO_PUBLIC_APPFLYER_ANDROID_APP_ID,
      onInstallConversionDataListener: true, //Optional
      onDeepLinkListener: true, //Optional
      timeToWaitForATTUserAuthorization: 10 //for iOS 14.5
    },
    (_) => {
      appsFlyer.setAppInviteOneLinkID(process.env.EXPO_PUBLIC_APPFLYER_ONE_LINK_TEMPLATE_ID ?? '-');
      appsFlyer.setOneLinkCustomDomains(
        [APPSFLYER_CONFIG.brandDomain],
        (result) => {
          if (__DEV__) console.log(result);
        },
        (error) => {
          console.error(error);
        }
      );
    },
    (error) => {
      console.error(error);
    }
  );
}

// Deeplinking after non-organic install
appsFlyer.onInstallConversionData((res) => {
  if (__DEV__) console.log('onInstallConversionData Data received:', res);

  if (res?.type === 'onInstallConversionDataLoaded' && res?.data?.af_status === 'Non-organic') {
    const deepLinkValue = res.data.deep_link_value ?? '';
    if (deepLinkValue?.length > 0) {
      const [routePath] = deepLinkValue.split('?');
      const params = parseQueryParams(deepLinkValue);

      if (__DEV__) console.log('Deferred Deeplink:', routePath, params);

      if (routePath === 'purchaseShow') {
        if (params['influencerCode']) {
          AppStorage.setItem('influencerCode', params['influencerCode']);
        }
        router.replace({
          pathname: '/home/npoTabs'
        });
        router.push('/purchaseShow');
        return;
      }

      if (routePath?.length > 0) {
        router.replace({
          pathname: '/home/npoTabs'
        });
        router.push(routePath);
      }
    }
  }
});

// Deeplinking
appsFlyer.onDeepLink((res) => {
  if (res?.deepLinkStatus !== 'NOT_FOUND' && res?.data.deep_link_value?.length > 0) {
    const deepLinkValue = res.data.deep_link_value ?? '';
    const dismissBrowser = res.data.dismissBrowser ?? '';

    const [routePath] = deepLinkValue.split('?');

    const params = parseQueryParams(deepLinkValue);

    if (routePath === 'donation') {
      if (dismissBrowser) {
        WebBrowser.dismissBrowser();
        return;
      }
      return;
    }

    if (routePath === '/(app)/purchaseShow' || routePath === 'purchaseShow') {
      if (params['influencerCode'] !== undefined) {
        AppStorage.setItem('influencerCode', params['influencerCode']);
        const deepLink = parseNativeDeepLink('/purchaseShow');
        if (deepLink) setPendingLink(deepLink);
      }

      if (params['clearInfluencerCode'] !== undefined) {
        AppStorage.deleteItem('influencerCode');
      }

      if (dismissBrowser) {
        WebBrowser.dismissBrowser();
        return;
      }
      return;
    }

    if (routePath === 'giftsList') {
      const giftFormData = res.data.giftFormData ?? '';

      if (router.canDismiss()) router.dismissAll();
      const deepLink = parseNativeDeepLinkWithParams('show/giftSummary', { giftFormData: giftFormData });
      if (deepLink) setPendingLink(deepLink);

      if (dismissBrowser) {
        WebBrowser.dismissBrowser();
        return;
      }
    }

    if (routePath === 'giftSeries') {
      if (dismissBrowser) {
        WebBrowser.dismissBrowser();
        return;
      }
      return;
    }

    if (
      routePath?.length > 0 &&
      routePath !== 'purchaseShow' &&
      routePath !== 'donation' &&
      routePath !== 'giftsList' &&
      routePath !== 'giftSeries'
    ) {
      const deepLink = parseNativeDeepLink(routePath);
      if (deepLink) setPendingLink(deepLink);
    }
  }
});

// Initialize Firebase Analytics for Web
if (Platform.OS === 'web') {
  initWebAnalytics();
}

function RootLayout() {
  const path = usePathname();

  // ToDO: Forced light theme, work on dark theme
  // const colorScheme = useColorScheme();
  // const theme =
  //   colorScheme === 'light' ? { ...DefaultTheme, colors: { ...DefaultTheme.colors, background: 'white' } } : DarkTheme;
  const theme = { ...DefaultTheme, colors: { ...DefaultTheme.colors, background: 'white' } };

  function onAppStateChange(status: AppStateStatus) {
    if (Platform.OS !== 'web') {
      focusManager.setFocused(status === 'active');
    }
  }

  useEffect(() => {
    const handler = AppState.addEventListener('change', onAppStateChange);
    return () => handler.remove();
  }, []);

  // Appsflyer Smart Banner
  // useEffect(() => {
  //   if (Platform.OS === 'web') {
  //     const script = document.createElement('script');

  //     script[script.innerText === undefined ? 'textContent' : 'innerText'] =
  //       `!function(t,e,n,s,a,c,i,o,p){t.AppsFlyerSdkObject=a,t.AF=t.AF||function(){(t.AF.q=t.AF.q||[]).push([Date.now()].concat(Array.prototype.slice.call(arguments)))},t.AF.id=t.AF.id||i,t.AF.plugins={},o=e.createElement(n),p=e.getElementsByTagName(n)[0],o.async=1,o.src="https://websdk.appsflyer.com?"+(c.length>0?"st="+c.split(",").sort().join(",")+"&":"")+(i.length>0?"af_id="+i:""),p.parentNode.insertBefore(o,p)}(window,document,"script",0,"AF","banners",{banners: {key: "7e200e9b-1935-4ab1-8c2e-e3fb26c81f32"}}); AF('banners', 'showBanner')`;

  //     document.documentElement.appendChild(script);

  //     return () => {
  //       document.documentElement.removeChild(script);
  //     };
  //   }
  // }, []);

  // Setting Notifications
  useEffect(() => {
    if (Platform.OS !== 'web') {
      // Set up the notification handler for the app
      Notifications.setNotificationHandler({
        handleNotification: async () => ({
          shouldShowBanner: true,
          shouldPlaySound: true,
          shouldSetBadge: false,
          shouldShowList: false
        })
      });

      // Handle user clicking on a notification and open the screen
      const handleNotificationClick = async (response: Notifications.NotificationResponse) => {
        if (__DEV__) console.log('Notificaction clicked:', response.notification.request.content?.data?.roomData);
        if (response.notification.request.content?.data?.roomData) {
          const roomData = response.notification.request.content?.data?.roomData;
          const roomJson = safeJsonParse<RoomData>(roomData.toString());

          if (roomJson?.id) {
            router.prefetch({
              pathname: '/messages',
              params: {
                roomId: roomJson?.id
              }
            });
          }
        } else if (response.notification.request.content?.data?.path) {
          const screen = response.notification.request.content.data.path;
          if (screen !== null) {
            router.navigate(screen as Href);
          }
        }
      };

      // Handle user opening the app from a notification (when the app is in the background)
      onNotificationOpenedApp(messaging(), (remoteMessage: FirebaseMessagingTypes.RemoteMessage) => {
        if (__DEV__) console.log('Notification caused app to open from background state:', remoteMessage?.data);
        if (remoteMessage?.data?.roomData) {
          const roomData = remoteMessage?.data?.roomData;
          const roomJson = safeJsonParse<RoomData>(roomData.toString());

          if (roomJson?.id) {
            router.prefetch({
              pathname: '/messages',
              params: {
                roomId: roomJson?.id
              }
            });
          }
        } else if (remoteMessage?.data?.path) {
          const screen = remoteMessage.data.path;
          if (screen !== null && screen && typeof screen === 'string') {
            router.navigate(screen as Href);
          }
        }
      });

      // Handle push notifications when the app is in the background
      setBackgroundMessageHandler(messaging(), async (remoteMessage: FirebaseMessagingTypes.RemoteMessage) => {
        if (__DEV__) console.log('Message handled in the background!', remoteMessage);
        const notification = {
          title: remoteMessage.notification?.title ?? '',
          body: remoteMessage.notification?.body ?? '',
          data: remoteMessage.data ?? {}
        };

        await Notifications.scheduleNotificationAsync({
          content: notification,
          trigger: null
        });
      });

      // Handle push notifications when the app is in the foreground
      const handlePushNotification = async (remoteMessage: FirebaseMessagingTypes.RemoteMessage) => {
        if (remoteMessage?.data?.path !== path) {
          const notification = {
            title: remoteMessage?.notification?.title ?? '',
            body: remoteMessage?.notification?.body ?? '',
            data: remoteMessage.data ?? {}
          };

          await Notifications.scheduleNotificationAsync({
            content: notification,
            trigger: null
          });
        }
      };

      // Listen for user clicking on a notification
      const notificationClickSubscription =
        Notifications.addNotificationResponseReceivedListener(handleNotificationClick);

      // Listen for push notifications when the app is in the foreground
      const unsubscribe = onMessage(messaging(), handlePushNotification);

      // Clean up the event listeners
      return () => {
        unsubscribe();
        notificationClickSubscription.remove();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for warm-boot deep links (when app is already open/in background) on Mobile
  useEffect(() => {
    if (Platform.OS !== 'web') {
      const handleDeepLink = (event: { url: string }) => {
        if (__DEV__) console.log('[Warm-boot DeepLink]:', event.url);
        try {
          const parsed = Linking.parse(event.url);
          if (parsed.path) {
            // Ensure path has a leading slash
            const cleanPath = parsed.path.startsWith('/') ? parsed.path : `/${parsed.path}`;
            const deepLink = parseNativeDeepLinkWithParams(
              cleanPath,
              (parsed.queryParams as Record<string, string>) ?? {}
            );
            if (deepLink) setPendingLink(deepLink);
          }
        } catch (error) {
          console.error('Failed to parse warm-boot deep link:', error);
        }
      };

      const subscription = Linking.addEventListener('url', handleDeepLink);
      return () => {
        subscription.remove();
      };
    }
  }, []);

  return (
    <GestureHandlerRootView style={styles.container}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider value={theme}>
          <AuthProvider>
            <NpoLoginScreenStateProvider>
              <EndFlowModalProvider>
                <InfoModalProvider>
                  <BottomSheetProvider>
                    <FiltersProvider>
                      <FavoritesProvider>
                        <PaperProvider>
                          <SafeAreaProvider>
                            <SystemBars style={'auto'} />
                            <Slot />
                            <EndFlowModal />
                            <InfoModal />
                            <Toast />
                            <SharedBottomSheet />
                          </SafeAreaProvider>
                        </PaperProvider>
                      </FavoritesProvider>
                    </FiltersProvider>
                  </BottomSheetProvider>
                </InfoModalProvider>
              </EndFlowModalProvider>
            </NpoLoginScreenStateProvider>
          </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </GestureHandlerRootView>
  );
}

// Wrap the Root Layout route component with `Sentry.wrap` to capture gesture info and profiling data.
// Using withProfiler to ensure Sentry it's initialized before rendering the RootLayout component
export default Sentry.withProfiler(RootLayout);

const styles = StyleSheet.create({
  container: {
    flex: 1
  }
});
